/**
 * History API client.
 *
 * The shape worth noticing is `HistoryEntry[] | null`. A record type the requester may not see comes
 * back as **null, not `[]`** — because "there is nothing here" and "this is not yours to see" are
 * different facts, and rendering an empty tab for the second would disclose that something exists
 * and is being withheld (docs/engineering/04-authorization.md).
 */

import { isNotificationKind } from '@pius/contracts';

import { request } from '../../api/client';

export interface HistoryEntry {
  id: string;
  at: string;
  actor: string;
  summary: string;
  /** Present where the record type has a previous and a new value. Rendered side by side. */
  before?: string | null;
  after?: string | null;
  detail?: string | null;
}

export type RecordType =
  'revision' | 'lifecycle' | 'capability' | 'notification' | 'export' | 'administrative';

export type History = Record<RecordType, HistoryEntry[] | null>;

export function fetchHistory(liturgyId: string): Promise<{ data: History }> {
  return request(`/liturgies/${liturgyId}/history`);
}

/**
 * Identifiers into English.
 *
 * Notification kinds and audit actions are stable identifiers on the wire — `history.controller.ts`
 * puts `row.kind` and `row.action` straight into `summary` — and a stable identifier is unreadable
 * in a list somebody scans. Anything unrecognised falls through unchanged rather than being hidden:
 * an unlabelled record is still a record.
 *
 * It lives beside the client rather than inside one screen because more than one screen renders
 * these entries. The Overview's activity card showed `planning.contribution_approved` verbatim
 * until it started calling this, which is what a second copy of the mapping would have cost.
 */
export function summarize(summary: string, type: RecordType, t: (key: string) => string): string {
  if (type === 'notification' && isNotificationKind(summary)) {
    return t(`notifications_kinds.${summary}`);
  }

  if (type === 'administrative') {
    const label = t(`audit_actions.${summary}`);
    if (label && label !== `audit_actions.${summary}`) {
      return label.charAt(0).toUpperCase() + label.slice(1);
    }
  }

  return summary;
}

/**
 * What each record type answers.
 *
 * Kept as prose rather than as bare labels because the six exist separately for a reason, and a
 * reader who does not know why will merge them in their head and then wonder why the notification
 * tab disagrees with the revision tab (L2-13 §4).
 */
export const RECORD_TYPES: {
  value: RecordType;
  label: string;
  question: string;
  note: string;
}[] = [
  {
    value: 'revision',
    label: 'Revisions',
    question: 'How did this planning decision evolve?',
    note: 'Every save, submission and review outcome, with what the value was and what it became.',
  },
  {
    value: 'lifecycle',
    label: 'Lifecycle',
    question: 'How did this celebration progress?',
    note: 'Movement between draft, planning, ready, finalized and archived.',
  },
  {
    value: 'capability',
    label: 'Access',
    question: 'How did delegated access change over time?',
    note: 'Links issued, replaced, revoked and expired — never the credential itself.',
  },
  {
    value: 'notification',
    label: 'Notifications',
    question: 'Was this communication attempted, and did it arrive?',
    note: 'Delivery is its own record: an email can fail while the decision it announced still stands.',
  },
  {
    value: 'export',
    label: 'Exports',
    question: 'Which planning state was exported?',
    note: 'That a snapshot was taken, not what it said.',
  },
  {
    value: 'administrative',
    label: 'Administrative',
    question: 'Who performed this governance action?',
    note: 'Written once and never edited afterwards.',
  },
];
